import { SamplingDecision } from '@opentelemetry/sdk-trace-base';
const LONG_MAX_BIGINT = BigInt('9223372036854775807');
const LONG_MIN_BIGINT = BigInt('-9223372036854775808');
const UNSIGNED_LONG_RANGE = BigInt('18446744073709551616');

// Matches (long)(ratio * Long.MAX_VALUE) in Java. Both Java double and JS Number
// are IEEE 754 float64, so the truncation is identical.
function computeIdUpperBound(ratio) {
  if (ratio <= 0) return LONG_MIN_BIGINT;
  if (ratio >= 1) return LONG_MAX_BIGINT;
  return BigInt(Math.trunc(ratio * Number(LONG_MAX_BIGINT)));
}

/**
 * A ratio-based sampler that uses the same algorithm as the Java backend
 * (OTel Java TraceIdRatioBasedSampler + HsOpenTelemetry's
 * toDeterministicUniformlyRandomId transformation).
 *
 * The stock OTel JS TraceIdRatioBasedSampler XOR-folds the trace ID into 32
 * bits, while the Java SDK uses the lower 64 bits after replacing the UUID
 * variant nibble. These two functions are uncorrelated, so a service with a
 * higher ratio can miss traces that a lower-ratio service sampled.
 *
 * By matching the Java algorithm, sampling decisions become correlated across
 * FE and BE: for any given trace ID, a higher ratio always samples a strict
 * superset of what a lower ratio samples.
 */
export class HsTraceIdRatioBasedSampler {
  constructor(ratio) {
    this._ratio = ratio;
    this._idUpperBound = computeIdUpperBound(ratio);
  }
  shouldSample(__context, traceId, __spanName, __spanKind, __attributes, __links) {
    // Guard against malformed trace IDs (OTel SDK always provides 32-char hex,
    // but a defensive check avoids an unhandled BigInt parse error).
    if (traceId.length < 18) {
      return {
        decision: SamplingDecision.NOT_RECORD
      };
    }
    // Matches Java's toDeterministicUniformlyRandomId: replace the UUID variant
    // nibble (char 16) with the random nibble at char 15 to get uniformly random
    // lower 64 bits, then use those for the ratio comparison.
    const effectiveHex = traceId[15] + traceId.substring(17);
    const unsigned = BigInt(`0x${effectiveHex}`);
    const signed = unsigned > LONG_MAX_BIGINT ? unsigned - UNSIGNED_LONG_RANGE : unsigned;

    // Match Java's Math.abs(long) - Long.MIN_VALUE overflows to itself.
    // Note: when ratio=1 the upper bound is LONG_MAX_BIGINT, so a trace whose
    // effectiveHex is exactly 7fffffffffffffff is not sampled (abs is not < bound).
    // This is intentional: it matches the same one-in-2^63 gap in the Java SDK.
    const abs = signed === LONG_MIN_BIGINT ? LONG_MIN_BIGINT : signed < 0n ? -signed : signed;
    return {
      decision: abs < this._idUpperBound ? SamplingDecision.RECORD_AND_SAMPLED : SamplingDecision.NOT_RECORD
    };
  }
  toString() {
    return `HsTraceIdRatioBasedSampler{${this._ratio}}`;
  }
}